/**
 * Services Page - Olympic Fit Wellness
 */
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check,
  Dumbbell,
  Apple,
  Heart,
  Brain,
  Users,
  Baby,
  Briefcase,
  Trophy
} from 'lucide-react';

export default function ServicesPage() {
  const mainServices = [
    {
      id: 'fitness',
      icon: Dumbbell,
      title: 'Fitness Training',
      description: 'Comprehensive fitness programs designed to help you achieve your goals, whether you\'re a beginner or elite athlete.',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800',
      features: [
        'Personal Training (1-on-1, partner, small group)',
        'Group Fitness Classes (500+ weekly)',
        'Virtual & On-Demand Workouts (1,000+)',
        'Sports-Specific Training',
        'Functional Fitness Programs',
        'Olympic Lifting Instruction'
      ]
    },
    {
      id: 'nutrition',
      icon: Apple,
      title: 'Nutrition Counseling',
      description: 'Expert nutrition guidance from Registered Dietitians to fuel your body for optimal performance and health.',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800',
      features: [
        'One-on-One Consultations with RDs',
        'Custom Meal Planning',
        'Macro Coaching & Tracking',
        'Grocery Store Tours',
        'Meal Prep Workshops',
        'Metabolic Testing & Body Composition'
      ]
    },
    {
      id: 'recovery',
      icon: Heart,
      title: 'Recovery & Spa',
      description: 'Advanced recovery services to help your body rest, repair, and perform at its best.',
      image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800',
      features: [
        'Infrared Saunas',
        'Hydromassage Beds',
        'Red Light Therapy',
        'Cryotherapy',
        'Sports Massage',
        'Compression Therapy'
      ]
    },
    {
      id: 'wellness',
      icon: Brain,
      title: 'Mental Wellness',
      description: 'Programs designed to support your mental health and help you find balance in your busy life.',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
      features: [
        'Guided Meditation Classes',
        'Stress Management Workshops',
        'Sleep Optimization Coaching',
        'Mindfulness Training',
        'Health & Wellness Coaching',
        'Breathing & Relaxation Techniques'
      ]
    }
  ];

  const specializedPrograms = [
    {
      icon: Trophy,
      title: 'Athletic Performance',
      description: 'Sport-specific training for competitive athletes looking to reach their peak.'
    },
    {
      icon: Users,
      title: 'Senior Wellness (55+)',
      description: 'Safe, effective programming designed to maintain independence and vitality.'
    },
    {
      icon: Baby,
      title: 'Pre/Post-Natal Fitness',
      description: 'Specialized programs for expecting and new mothers.'
    },
    {
      icon: Briefcase,
      title: 'Corporate Wellness',
      description: 'Custom B2B wellness solutions for businesses and their teams.'
    }
  ];

  return (
    <div className="bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">OUR SERVICES</span>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Comprehensive Wellness Services
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Everything you need for complete wellness—fitness, nutrition, recovery, and mental health—all under one roof.
          </p>
        </div>
      </section>

      {/* Main Services */}
      {mainServices.map((service, idx) => (
        <section 
          key={service.id} 
          id={service.id}
          className={`py-20 lg:py-28 ${idx % 2 === 1 ? 'bg-neutral-50' : ''}`}
        >
          <div className="container mx-auto px-4 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${idx % 2 === 1 ? '' : ''}`}>
              <div className={idx % 2 === 1 ? 'order-2' : ''}>
                <div className="w-16 h-16 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-[#3B82F6]" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
                  {service.title}
                </h2>
                <p className="text-neutral-600 mb-8">
                  {service.description}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-neutral-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/membership" 
                  className="inline-flex items-center gap-2 bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors"
                >
                  GET STARTED <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className={idx % 2 === 1 ? 'order-1' : ''}>
                <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Specialized Programs */}
      <section className="py-20 lg:py-28 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">SPECIALIZED PROGRAMS</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Programs for Every Journey
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              Whether you're an athlete, senior, new parent, or corporate team—we have programs designed for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializedPrograms.map((program, idx) => (
              <div key={idx} className="bg-neutral-800 p-8 rounded-lg">
                <div className="w-12 h-12 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center mb-6">
                  <program.icon className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-3">
                  {program.title}
                </h3>
                <p className="text-neutral-400 text-sm">
                  {program.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kids Club */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800"
                  alt="Kids Wellness Club"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">FOR FAMILIES</span>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
                Kids Wellness Club
              </h2>
              <p className="text-neutral-600 mb-6">
                Safe, supervised childcare while you work out. Our Kids Wellness Club provides age-appropriate physical activities, holiday camps, and more.
              </p>
              
              <ul className="space-y-3 mb-8">
                {['Safe, supervised environment', 'Age-appropriate activities', 'Holiday and summer camps', 'Birthday party packages'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-neutral-700">
                    <Check className="w-5 h-5 text-[#3B82F6]" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-[#3B82F6] transition-colors">
                LEARN MORE ABOUT KIDS CLUB <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-4">
            Ready to Experience Complete Wellness?
          </h2>
          <p className="text-neutral-600 mb-8 max-w-xl mx-auto">
            Start your 7-day free trial and get access to all our services.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/membership" className="bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors inline-flex items-center gap-2">
              START FREE TRIAL <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/locations" className="border border-neutral-300 text-neutral-700 font-semibold px-8 py-4 text-sm tracking-wider hover:border-black transition-colors">
              FIND A LOCATION
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
